import React, { useMemo } from 'react'
import { Box, Typography, Stack } from '@mui/material'
import { CardItem } from '../types'
import HorizontalBarChart from '../charts/HorizontalBarChart'
import { OverflowTooltipWrapper } from '../../OverflowTooltipWrapper/OverflowTooltipWrapper'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import Link from '@mui/material/Link'
import { ValueMessage } from '../../../../components/ValueNarrativesView/queries/useGetValueNarrativesQuery'
import pluralize from 'pluralize'
import { toDisplayValue } from '../../../utils/displayValue'

export const CardRow: React.FC<{
    item: CardItem
    viewMode: 'list' | 'graphs'
    index: number
    subtitle?: string
    showValue: boolean
    maxValue: number
}> = ({ item, viewMode, index, subtitle, showValue, maxValue }) => {
    const history = useHistory()

    const linkingPathname = useMemo(() => {
        if (!item.item?._id) return null

        switch (item.type) {
            case 'Activity':
                return '/activities'
            case 'Evidence':
                return '/evidence'
            case 'ValueMessage':
                const valueMessage = item.item as ValueMessage
                if (!valueMessage?.categoryId || !valueMessage?.narrativeId) return null

                return (
                    '/narratives/categories/' +
                    valueMessage.categoryId +
                    '/narratives/' +
                    valueMessage.narrativeId +
                    '/messages/' +
                    valueMessage._id
                )
            default:
                return null
        }
    }, [item.type])

    const unit = pluralize(subtitle, item.value)

    return (
        <Stack direction="column" gap={0.5}>
            {viewMode === 'graphs' && (
                <OverflowTooltipWrapper
                    placement="top-start"
                    arrow
                    title={
                        <Stack gap={1} overflow="scroll">
                            <Typography
                                variant="14x400"
                                color="figma.grayscale.textHier1"
                                sx={{
                                    whiteSpace: 'pre-line',
                                    overflow: 'scroll',
                                    maxHeight: '50vh',
                                }}
                            >
                                {item.label}
                            </Typography>
                        </Stack>
                    }
                    componentsProps={{
                        tooltip: {
                            sx: {
                                backgroundColor: 'figma.grayscale.tier7',
                                px: 2,
                                py: 1,
                                borderRadius: '6px',
                                boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                            },
                        },
                        arrow: {
                            sx: {
                                color: 'figma.grayscale.tier7',
                            },
                        },
                    }}
                >
                    <Link
                        component={RouterLink}
                        underline="none"
                        color="figma.grayscale.tier3"
                        mr={viewMode === 'graphs' ? 1.5 : 0}
                        to={{
                            pathname: linkingPathname,
                            autoOpenData: {
                                isAutoOpenPanel: true,
                                autoOpenData: item.item,
                                previousDataStack: [
                                    {
                                        path: history.location.pathname,
                                    },
                                ],
                            },
                        }}
                    >
                        <Typography
                            variant="18x400"
                            color="inherit"
                            noWrap
                            sx={
                                linkingPathname
                                    ? {
                                          cursor: 'pointer',
                                          '&:hover': {
                                              color: 'figma.primary',
                                              textDecoration: 'underline',
                                          },
                                      }
                                    : {
                                          cursor: 'default',
                                      }
                            }
                        >
                            {toDisplayValue(item.label)}
                        </Typography>
                    </Link>
                </OverflowTooltipWrapper>
            )}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={viewMode === 'graphs' ? 1 : 3}
            >
                {viewMode === 'list' ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ overflow: 'hidden', minWidth: 0 }}>
                        <Typography
                            variant="16x400"
                            color="figma.grayscale.textHier2"
                            width="24px"
                            textAlign="center"
                            flexShrink={0}
                        >
                            {`${index + 1}.`}
                        </Typography>
                        <OverflowTooltipWrapper
                            placement="top-start"
                            arrow
                            title={
                                <Stack gap={1} overflow="scroll">
                                    <Typography
                                        variant="14x400"
                                        color="figma.grayscale.textHier1"
                                        sx={{
                                            whiteSpace: 'pre-line',
                                            overflow: 'scroll',
                                            maxHeight: '50vh',
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Stack>
                            }
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: 'figma.grayscale.tier7',
                                        px: 2,
                                        py: 1,
                                        borderRadius: '6px',
                                        boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: 'figma.grayscale.tier7',
                                    },
                                },
                            }}
                        >
                            <Link
                                component={RouterLink}
                                underline="none"
                                color="secondary"
                                to={{
                                    pathname: linkingPathname,
                                    autoOpenData: {
                                        isAutoOpenPanel: true,
                                        autoOpenData: item.item,
                                        previousDataStack: [
                                            {
                                                path: history.location.pathname,
                                            },
                                        ],
                                    },
                                }}
                            >
                                <Typography
                                    variant="16x400"
                                    color="figma.grayscale.textHier1"
                                    noWrap
                                    sx={
                                        linkingPathname
                                            ? {
                                                  cursor: 'pointer',
                                                  '&:hover': {
                                                      color: 'figma.primary',
                                                      textDecoration: 'underline',
                                                  },
                                              }
                                            : { cursor: 'default' }
                                    }
                                >
                                    {toDisplayValue(item.label)}
                                </Typography>
                            </Link>
                        </OverflowTooltipWrapper>
                    </Stack>
                ) : (
                    <Stack direction="row" alignItems="center" gap={1} flex={1} minWidth={0}>
                        <Box sx={{ width: '100%', height: '8px' }}>
                            <HorizontalBarChart values={[{ value: item.value }]} maxValue={maxValue} />
                        </Box>
                    </Stack>
                )}

                {showValue &&
                    (viewMode === 'list' ? (
                        <Typography variant="20x500" color="figma.grayscale.textHier1" whiteSpace="pre">
                            {item.value} {`${unit}${pluralize.isPlural(unit) ? '' : '  '}`}
                        </Typography>
                    ) : (
                        <Stack direction="row" justifyContent="center" width="46px">
                            <OverflowTooltipWrapper
                                placement="top-start"
                                arrow
                                title={
                                    <Stack gap={1} overflow="scroll">
                                        <Typography variant="20x500" color="figma.grayscale.textHier1" whiteSpace="pre">
                                            {item.value}
                                        </Typography>
                                    </Stack>
                                }
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            backgroundColor: 'figma.grayscale.tier7',
                                            px: 2,
                                            py: 1,
                                            borderRadius: '6px',
                                            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                                        },
                                    },
                                    arrow: {
                                        sx: {
                                            color: 'figma.grayscale.tier7',
                                        },
                                    },
                                }}
                            >
                                <Typography variant="20x500" color="figma.grayscale.textHier1" noWrap>
                                    {item.value}
                                </Typography>
                            </OverflowTooltipWrapper>
                        </Stack>
                    ))}
            </Stack>
        </Stack>
    )
}
